import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  useStripe,
} from '@stripe/react-stripe-js';

import useNavigateWithSearch from '../../../../../hooks/useNavigateWithSearch';

import LoadingBar from '../../LoadingBar';
import MessageContent from '../../MessageContent';
import { getGenericError } from '../../../utils/errors';
import {
  getMembershipSubscription,
  updateMembershipSubscription,
} from '../../../utils/service';
import {
  ROUTE_MANAGE_MEMBERSHIP, ROUTE_MEMBERSHIP_PAYMENT_METHOD,
} from '../../../config/routes';
import CheckCircleIcon from '../../../images/check-circle-icon.svg';
import IconCloudy from '../../../images/icon-cloudy.svg';
import IconHourglass from '../../../images/icon-hourglass.svg';

import './MembershipPaymentMethodConfirmation.scss';

const STRIPE_SETUP_INTENT_STATUS = {
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
  REQUIRES_PAYMENT_METHOD: 'requires_payment_method',
};

// Must be used inside stripe's Elements component
const MembershipPaymentMethodConfirmation = ({
  setupIntentClientSecret,
}) => {
  const stripe = useStripe();
  const navigateWithSearch = useNavigateWithSearch();

  const [loading, setLoading] = useState(true);
  const [setupIntentStatus, setSetupIntentStatus] = useState(null);
  const [error, setError] = useState(null);

  const handleUpdateMembershipSubscription = useCallback(async () => {
    setLoading(true);
    setSetupIntentStatus(null);
    setError(null);

    const {
      setupIntent,
      error: setupIntentError,
    } = await stripe.retrieveSetupIntent(setupIntentClientSecret);

    if (setupIntentError || !setupIntent.status) {
      setError(setupIntentError.message || getGenericError());
      setLoading(false);
      return;
    }

    setSetupIntentStatus(setupIntent.status);

    switch (setupIntent.status) {
      case STRIPE_SETUP_INTENT_STATUS.SUCCEEDED:
        break;
      case STRIPE_SETUP_INTENT_STATUS.PROCESSING:
        setLoading(false);
        return;
      case STRIPE_SETUP_INTENT_STATUS.REQUIRES_PAYMENT_METHOD:
      default:
        setError('Failed to process payment details. Please try another payment method.');
        setSetupIntentStatus(null);
        setLoading(false);
        return;
    }

    const {
      data: subscriptionData,
      error: subscriptionError,
    } = await getMembershipSubscription();

    if (subscriptionError || !subscriptionData?.uid) {
      setError(subscriptionError?.message || getGenericError());
      setLoading(false);
      return;
    }

    const {
      error: updateSubscriptionError,
    } = await updateMembershipSubscription(
      subscriptionData.uid,
      setupIntent.payment_method,
    );

    if (updateSubscriptionError) {
      setError(updateSubscriptionError.message || getGenericError());
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [stripe, setupIntentClientSecret]);

  const {
    image,
    message,
    buttonText,
    buttonOnClick,
  } = useMemo(() => {
    switch (setupIntentStatus) {
      case STRIPE_SETUP_INTENT_STATUS.SUCCEEDED:
        return {
          image: CheckCircleIcon,
          message: 'Success! Your payment method has been updated.',
          buttonText: 'Back to membership',
          buttonOnClick: () => navigateWithSearch(ROUTE_MANAGE_MEMBERSHIP, {}, true),
        };
      case STRIPE_SETUP_INTENT_STATUS.PROCESSING:
        return {
          image: IconHourglass,
          message: 'Processing payment method details. We\'ll update you when processing is complete.',
          buttonText: 'Check again',
          buttonOnClick: handleUpdateMembershipSubscription,
        };
      case STRIPE_SETUP_INTENT_STATUS.REQUIRES_PAYMENT_METHOD:
        return {
          image: IconCloudy,
          message: 'Failed to process payment method details. Please try another payment method.',
          buttonText: 'Try again',
          buttonOnClick: () => navigateWithSearch(ROUTE_MEMBERSHIP_PAYMENT_METHOD, {}, true),
        };
      default:
        return {};
    }
  }, [setupIntentStatus, handleUpdateMembershipSubscription]);

  useEffect(() => {
    if (stripe && setupIntentClientSecret) {
      handleUpdateMembershipSubscription();
    }
  }, [stripe, setupIntentClientSecret, handleUpdateMembershipSubscription]);

  return (
    <div className="MembershipPaymentMethodConfirmation">
      {loading ? (
        <LoadingBar />
      ) : (
        <MessageContent
          image={image}
          title={message || error}
          cta={{
            text: buttonText,
            onClick: buttonOnClick,
          }}
        />
      )}
    </div>
  );
};

export default MembershipPaymentMethodConfirmation;
